import styled from '@emotion/styled'

import { useTheme } from '@emotion/react'

import type { FC } from 'react'
import { useMemo } from 'react'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Footer from '@segment/templates/Footer'
import NavigationV2 from '@segment/templates/Navigation2'

import LogoTiles from '@segment/templates/LogoTiles'

import { TilesGridWrapper } from '@segment/templates/LogoTiles/LogoTiles.views'

import {
  type GroupedFilters,
  useFiltersQuery,
  useFeaturedEventQuery,
  useEventsQuery,
} from '@segment/api/routes/events/queries'

import Layout from '@segment/next-shared/layout'

import Hero from './components/Hero'

import { useFilters } from './data/useFilters'

import Filters from './components/Filters'

import EventResults from './components/EventResults'

import StayInTheKnow from './components/StayInTheKnow'

import ogImageJpg from './assets/OgEvents.jpg'

const EventsView: FC = () => {
  const theme = useTheme()

  const layoutData = useMemo(
    () => ({
      title: `Events | Twilio Segment`,
      description: `Stay up to date with the Twilio Segment events`,
      og: ogImageJpg,
      backgroundColor: theme.palette.neutrals.white[100],
    }),
    [theme]
  )

  const { data: filters } = useFiltersQuery({ args: [] })
  const { data: featuredEvent } = useFeaturedEventQuery({ args: [] })
  const groupedFilterEntries = Object.entries((filters as GroupedFilters) || {})
  const { filtersState, selectHandlers } = useFilters(groupedFilterEntries)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialFilters = useMemo(() => filtersState, [])
  const { data: initialEvents } = useEventsQuery({
    args: [{ filters: initialFilters }],
  })
  const { data: filteredData, isFetching } = useEventsQuery({
    args: [{ filters: filtersState }],
  })
  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={layoutData.title}
        pageDescription={layoutData.description}
        pageOG={layoutData.og}
        bodyBackground={layoutData.backgroundColor}
      >
        <NavigationV2 />
        <Hero data={featuredEvent || null} />
        <Main>
          <Filters
            filters={groupedFilterEntries}
            onFilterChange={(filterId, selectedOptions) => selectHandlers[filterId](selectedOptions)}
            disabled={initialEvents?.length === 0}
          />
          <EventResults initialEvents={initialEvents} events={filteredData} isLoading={isFetching} />
          <StayInTheKnow />
          <LogoWrapper>
            <LogoTiles />
          </LogoWrapper>
        </Main>
        <Footer />
      </Layout>
    </AppearanceProvider>
  )
}

const Main = styled.main`
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};
`

const LogoWrapper = styled.div`
  ${TilesGridWrapper} {
    max-width: 100%;
  }
`

export default EventsView
